import dayjs from "dayjs";
import i18n, { type ResourceKey } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import { merge, get } from "lodash-es";
import { LOCAL_STORAGE_LANG } from "@/constants/normal";

let areaName: string;
export const initI18n = async (defaultLocale: string, area: string) => {
  areaName = area;
  const languageDetector = new LanguageDetector();
  const persistLang = localStorage.getItem(LOCAL_STORAGE_LANG) || defaultLocale;

  const { mergedMessage, defaultVariables } = await getMergedMessage(persistLang);

  languageDetector.addDetector({
    name: "custom-detector",
    lookup() {
      return persistLang;
    },
    cacheUserLanguage(lng, _options) {
      localStorage.setItem(LOCAL_STORAGE_LANG, lng);
    },
  });

  await i18n
    .use(ICU)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(languageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
      debug: true,
      load: "currentOnly",
      fallbackLng: defaultLocale,
      resources: {
        [persistLang]: { translation: mergedMessage },
      },
      interpolation: {
        defaultVariables,
      },
      detection: {
        order: ["custom-detector"],
        caches: ["custom-detector"],
      },
    });
  setLanAttr(i18n.language);
};

export async function changeLanguage(lng: string) {
  const formatLang = formatLanguageCode(lng);
  const { mergedMessage, defaultVariables } = await getMergedMessage(formatLang);

  i18n.addResourceBundle(formatLang, "translation", mergedMessage);
  i18n.options.interpolation = {
    ...i18n.options.interpolation,
    defaultVariables,
  };

  await i18n.changeLanguage(formatLang);
  setLanAttr(i18n.language);
}

async function getMergedMessage(lng: string) {
  let messageByArea: ResourceKey = {};
  try {
    messageByArea = (await import(`../i18n/locales/${areaName}/${lng}.json`)).default;
  } catch (error) {
    const _error = error;
    console.warn("Specified country i18n not found");
  }
  const baseMessage = (await import(`../i18n/locales/${lng}.json`)).default;
  const mergedMessage = merge(baseMessage, messageByArea) as ResourceKey;

  const defaultVariables = {
    appName: get(mergedMessage, "APP.NAME") as string,
    appShortName: get(mergedMessage, "APP.SHORT_NAME") as string,
  };

  return { mergedMessage, defaultVariables };
}

function formatLanguageCode(langCode: string): string {
  return langCode.replace(/-([a-z]{2})$/i, (_match, group) => {
    return `-${group.toUpperCase()}`;
  });
}

function setLanAttr(lng: string) {
  document.documentElement.setAttribute("lang", lng);
  dayjs.locale(lng);
}

export default i18n;
